import { useEffect, useRef, useState } from "react";
import { submitFeatureRequest } from "../lib/api";

interface Props {
  open: boolean;
  onClose: () => void;
}

const TITLE_MAX = 140;
const BODY_MAX = 4000;

/**
 * Lightweight modal for posting a feature wish straight to the project's
 * GitHub issue tracker. The worker handles auth (re-uses the CF-Access
 * identity for attribution) and rate-limiting; this component focuses on
 * the form, validation hints, and the success / error UI.
 *
 * Layout: focuses the title on open, supports `Esc` to close, traps clicks
 * inside the panel so dragging accidentally on the backdrop doesn't
 * dismiss in-progress text.
 */
export function FeatureRequestModal({ open, onClose }: Props) {
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [status, setStatus] = useState<
    | { kind: "idle" }
    | { kind: "submitting" }
    | { kind: "success"; url?: string; number?: number }
    | { kind: "error"; message: string }
  >({ kind: "idle" });
  const titleRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!open) return;
    // Reset state on open so re-opening after a success doesn't show the old
    // success banner.
    setStatus({ kind: "idle" });
    setTitle("");
    setBody("");
    const timer = setTimeout(() => titleRef.current?.focus(), 60);
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => {
      clearTimeout(timer);
      document.removeEventListener("keydown", onKey);
    };
  }, [open, onClose]);

  if (!open) return null;

  const titleTooShort = title.trim().length > 0 && title.trim().length < 5;
  const titleTooLong = title.length > TITLE_MAX;
  const bodyTooLong = body.length > BODY_MAX;
  const canSubmit =
    !titleTooShort && !titleTooLong && !bodyTooLong && title.trim().length >= 5 && status.kind !== "submitting";

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSubmit) return;
    setStatus({ kind: "submitting" });
    try {
      const res = await submitFeatureRequest({ title: title.trim(), body: body.trim() });
      setStatus({ kind: "success", url: res.issueUrl, number: res.issueNumber });
    } catch (err) {
      setStatus({ kind: "error", message: (err as Error).message });
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="fr-title"
      onClick={onClose}
    >
      <div
        className="w-full max-w-lg rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="flex items-center justify-between px-5 py-3 border-b border-slate-200 dark:border-slate-800">
          <h2 id="fr-title" className="text-base font-semibold">
            Feature anfragen
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="text-slate-500 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-100 text-xl leading-none"
            aria-label="Schließen"
          >
            ×
          </button>
        </header>

        {status.kind === "success" ? (
          <div className="p-5 space-y-3">
            <p className="text-sm text-emerald-700 dark:text-emerald-300">
              Danke! Deine Anfrage wurde als GitHub-Issue erstellt.
            </p>
            {status.url && (
              <p className="text-xs">
                <a
                  href={status.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sky-600 dark:text-sky-400 hover:underline break-all"
                >
                  #{status.number} → {status.url}
                </a>
              </p>
            )}
            <div className="pt-2">
              <button
                type="button"
                onClick={onClose}
                className="rounded-md bg-slate-900 dark:bg-white dark:text-slate-900 text-white px-3 py-1.5 text-sm font-medium hover:bg-slate-800 dark:hover:bg-slate-200"
              >
                Schließen
              </button>
            </div>
          </div>
        ) : (
          <form onSubmit={submit} className="p-5 space-y-4">
            <p className="text-xs text-slate-500 dark:text-slate-400 leading-snug">
              Beschreibe kurz, welches Feature du dir wünschst oder welche Verbesserung dir
              auffällt. Die Anfrage wird als GitHub-Issue im{" "}
              <code className="text-[11px]">montel-mcp</code>-Repo erstellt — mit deiner
              E-Mail-Adresse als Absender.
            </p>

            <label className="block space-y-1">
              <span className="text-xs uppercase tracking-wide text-slate-500 dark:text-slate-400 font-medium">
                Titel
              </span>
              <input
                ref={titleRef}
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                maxLength={TITLE_MAX + 20}
                placeholder="z. B. Wochenmittel-Linie im Spot-Chart"
                className="block w-full rounded-md border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-950 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-sky-500"
                required
              />
              <span
                className={`block text-[10px] ${
                  titleTooShort || titleTooLong
                    ? "text-rose-600 dark:text-rose-300"
                    : "text-slate-400 dark:text-slate-500"
                }`}
              >
                {titleTooShort
                  ? "Mindestens 5 Zeichen"
                  : titleTooLong
                    ? `Maximal ${TITLE_MAX} Zeichen (aktuell ${title.length})`
                    : `${title.length}/${TITLE_MAX}`}
              </span>
            </label>

            <label className="block space-y-1">
              <span className="text-xs uppercase tracking-wide text-slate-500 dark:text-slate-400 font-medium">
                Beschreibung <span className="opacity-70 normal-case">(optional)</span>
              </span>
              <textarea
                value={body}
                onChange={(e) => setBody(e.target.value)}
                rows={5}
                maxLength={BODY_MAX + 50}
                placeholder="Was soll passieren? Welche Daten sind nützlich?"
                className="block w-full rounded-md border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-950 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-sky-500 resize-y"
              />
              <span
                className={`block text-[10px] ${
                  bodyTooLong ? "text-rose-600 dark:text-rose-300" : "text-slate-400 dark:text-slate-500"
                }`}
              >
                {bodyTooLong ? `Maximal ${BODY_MAX} Zeichen` : `${body.length}/${BODY_MAX}`}
              </span>
            </label>

            {status.kind === "error" && (
              <p className="text-xs text-rose-700 dark:text-rose-300">{status.message}</p>
            )}

            <div className="flex justify-end gap-2 pt-2">
              <button
                type="button"
                onClick={onClose}
                className="rounded-md px-3 py-1.5 text-sm font-medium text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800"
              >
                Abbrechen
              </button>
              <button
                type="submit"
                disabled={!canSubmit}
                className="rounded-md bg-slate-900 dark:bg-white dark:text-slate-900 text-white px-3 py-1.5 text-sm font-medium hover:bg-slate-800 dark:hover:bg-slate-200 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {status.kind === "submitting" ? "Sende…" : "Absenden"}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
